import { Box, Typography, LinearProgress, Button } from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import { ThemePalette } from 'mui.theme';
import { getAssetNoOfNotifications } from 'views/TirePressure/util';
import { type Asset } from 'types/asset.types';
import { useAppSelector } from 'store/hook';
import { addLiveDataToAssets } from 'views/FleetOverview/helpers/asset.helpers';
import { clientThemePalette, CLIENT_THEME } from 'utils/clientTheme';
import { useTranslation } from 'react-i18next';

const AssetNotifications = ({
  enableDarkTheme,
  data,
  selectedAsset,
  setSelectedAsset,
  notifications,
}: {
  enableDarkTheme: boolean;
  data: any[];
  selectedAsset: {
    bumperId: string;
    deviceId: string;
  } | null;
  setSelectedAsset: any;
  notifications: any[];
}) => {
  const { t } = useTranslation();
  const { latestTagData } = useAppSelector((state) => state.deviceReducer);
  const { assets } = useAppSelector((state) => state.assetReducer);
  const assetsWithLiveData = addLiveDataToAssets(assets, latestTagData);

  const colorMode = enableDarkTheme ? 'dark' : 'light';
  const themeColors = clientThemePalette[CLIENT_THEME][colorMode];

  const fontColor = themeColors.textPrimary;

  const displayData = getAssetNoOfNotifications(notifications, data).sort(
    (a, b) => b.count - a.count
  );

  return (
    <Box
      sx={{
        marginTop: '10px',
        height: '500px',
        borderRadius: '8px',
        border: `1px solid ${themeColors.cardBorderColor}`,
        padding: '10px',
        overflow: 'scroll',
      }}
    >
      <Typography
        sx={{
          color: fontColor,
          fontSize: '14px',
          fontWeight: '700',
        }}
      >
        {t('assets_with_most_notifications')}
      </Typography>
      {selectedAsset && (
        <Box
          sx={{
            display: 'flex',
            alignContent: 'center',
            alignItems: 'center',
            marginTop: '10px',
          }}
        >
          <Typography
            sx={{
              fontSize: '12px',
              color: fontColor,
              marginRight: '10px',
            }}
          >
            {t('showing_insights_for_asset')} {selectedAsset.bumperId}
          </Typography>
          <Button
            sx={{
              backgroundColor: themeColors.primary,
              color: themeColors.textPrimary,
              textTransform: 'none',
              height: '40px',
              paddingLeft: '15px',
              paddingRight: '15px',
              fontSize: '12px',
              '&:hover': {
                backgroundColor: enableDarkTheme
                  ? themeColors.boxBackground
                  : themeColors.boxBackground,
              },
            }}
            onClick={() => {
              setSelectedAsset(null);
            }}
          >
            {t('reset')}
          </Button>
        </Box>
      )}
      <Box
        sx={{
          marginTop: '10px',
        }}
      >
        {displayData.map((item, index) => {
          return (
            <Box
              key={index}
              sx={{
                marginBottom: '20px',
                display: 'flex',
                flexDirection: 'column',
                gap: '10px',
                cursor: 'pointer',
                paddingLeft: '10px',
                paddingRight: '10px',
                paddingBottom: '10px',
                border:
                  selectedAsset?.bumperId === item.bumperId
                    ? `1px solid ${themeColors.cardBorderColor}`
                    : 'none',
                borderRadius:
                  selectedAsset?.bumperId === item.bumperId ? '4px' : 'none',
              }}
              onClick={() => {
                setSelectedAsset({
                  bumperId: item.bumperId,
                  deviceId: item.deviceId,
                });
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <Box
                  sx={{
                    marginTop: '10px',
                    marginRight: '5px',
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: '14px',
                      fontWeight: '600',
                      color: fontColor,
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'left',
                      }}
                    >
                      {item.bumperId}
                    </Box>
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: '12px',
                      color: ThemePalette.typography.grey,
                      fontWeight: '500',
                      marginTop: '4px',
                    }}
                  >
                    {item.makeModel}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    width: '75%',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                  }}
                >
                  <Box sx={{ flex: 1 }}>
                    <LinearProgress
                      variant="determinate"
                      value={(item.value / 10) * 100}
                      sx={{
                        height: 6,
                        borderRadius: 4,
                        bgcolor: 'rgba(255, 255, 255, 0.12)',
                        '& .MuiLinearProgress-bar': {
                          bgcolor: themeColors.primary,
                          borderRadius: 4,
                        },
                      }}
                    />
                  </Box>
                  <Typography
                    sx={{
                      fontSize: '12px',
                      fontWeight: '600',
                      color: ThemePalette.typography.grey,
                      marginLeft: '10px',
                    }}
                  >
                    {item.count}
                  </Typography>
                  <InfoIcon
                    sx={{
                      fontSize: '14px',
                      color: ThemePalette.typography.grey,
                    }}
                  />
                </Box>
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default AssetNotifications;
